/**
 * 跨屏跳转。**只为「点一个客户 → 去他的详情」这一件事存在。**
 *
 * 原型里日历格子、动作卡上的客户名、知识库里的客户专属资料,点下去都会跳到
 * 「我的客户」并选中那一家(`goCustomer(idx)`)。这不是装饰:
 * 行动导航是「系统告诉你该干嘛」的一屏,而干这件事要去客户详情 ——
 * 中间让人自己去菜单里翻、再在一百多行里找那家公司,这一跳就断了。
 *
 * 做成一个二十行的订阅点,而不是引路由或状态库:整个应用只有这一处要跨屏传值,
 * 为它引一套抽象是拿复杂度换一个变量。与 `currentCustomer.ts` 同样的取舍。
 */
export interface NavIntent {
  readonly tab: "customers" | "knowledge" | "profile" | "profileSetup" | "fields";
  readonly customerId?: string;
  /** 跳到知识库时定位到哪个分区(原型五分区那排「管理」)。 */
  readonly partition?: string;
  readonly documentId?: string;
  readonly chunkId?: string;
  readonly knowledgeSection?: "entries" | "originals";
  /** 带上就直接展开辩证共识面板(原型的 goCustomerDialectic)。 */
  readonly openConsensusFor?: string;
  /** 每次跳转都换一个,保证「点同一家两次」也能触发。 */
  readonly nonce: number;
}

let intent: NavIntent | undefined;
const listeners = new Set<() => void>();

export function goCustomers(): void {
  intent = { tab: "customers", nonce: Date.now() };
  for (const listener of listeners) listener();
}

/** 跳到某个客户的详情。`cardId` 给了就顺带展开那张卡的辩证面板。 */
export function goCustomer(customerId: string, cardId?: string): void {
  intent = {
    tab: "customers",
    customerId,
    ...(cardId ? { openConsensusFor: cardId } : {}),
    nonce: Date.now(),
  };
  for (const l of listeners) l();
}

/** 跳到知识库并定位到某个分区(知识引擎五分区那排「管理」)。 */
export function goKnowledge(partition?: string, documentId?: string, chunkId?: string, knowledgeSection?: "entries" | "originals"): void {
  intent = { tab: "knowledge", partition, documentId, chunkId, knowledgeSection, nonce: Date.now() };
  for (const l of listeners) l();
}

export function getNavIntent(): NavIntent | undefined {
  return intent;
}

/**
 * 消费掉这次跳转。
 *
 * **必须消费**,否则从客户页切到别的屏再切回来,它会把你又弹回上次那个客户 ——
 * 而你明明是想看别人。
 */
export function consumeNavIntent(): void {
  intent = undefined;
  for (const l of listeners) l();
}

export function subscribeNav(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function goProfile():void { intent={tab:"profile",nonce:Date.now()};for(const listener of listeners)listener(); }
/** 画像页「调整客户信息表」:画像规则只能用已有的字段,缺字段要先去系统设置补。 */
export function goFields():void { intent={tab:"fields",nonce:Date.now()};for(const listener of listeners)listener(); }
/** 去「系统设置 → 画像建立与优化」:浏览页的标准是只读的,建立和调整都在那里。 */
export function goProfileSetup():void { intent={tab:"profileSetup",nonce:Date.now()};for(const listener of listeners)listener(); }
